import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Brain, Music, BookOpen, Users, MessageCircle, ChevronDown, ChevronUp, ExternalLink, Play, Sparkles } from 'lucide-react';
import UnifiedMenu from '../components/UnifiedMenu';
import axios from 'axios';
import { API } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const HERO_IMG = 'https://images.unsplash.com/photo-1767460959466-9f2e6f44b5f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHx5b2dhJTIwemVuJTIwZ2FyZGVuJTIwY2FsbSUyMHNlcmVuZSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzA1MTQ1MDR8MA&ixlib=rb-4.1.0&q=85';

// i18n for About Us content
const ABOUT_TEXT = {
  en: {
    hero: 'Your Emotional Wellness, Reimagined',
    heroSub: 'Science-backed emotional health platform to understand, track, and nurture your inner world.',
    howItWorks: 'How It Works', step1: 'Check In', step2: 'Reflect', step3: 'Grow',
    whatYouCanDo: 'What You Can Do',
    uc1: 'Daily Check-ins', uc1d: 'Identify how you feel. Patterns emerge over time.',
    uc2: 'Therapy Companion', uc2d: 'Share emotion logs with your therapist.',
    uc3: 'Mood-Based Music', uc3d: 'Songs that match or shift your emotional state.',
    uc4: 'AI Companion', uc4d: 'Talk to Mojo anytime — empathetic & judgment-free.',
    uc5: 'Journaling', uc5d: 'Process feelings deeply with guided reflection.',
    uc6: 'Community', uc6d: "See emotional trends in your area. You're not alone.",
    inspiration: "This Week's Inspiration", science: 'The Science', coaches: 'Learn from the Best',
    floatingBtn: 'How are you feeling?',
    hoffman: 'The Hoffman Feeling Categories', hoffmanDesc: '18 nuanced emotional categories that go beyond "happy" or "sad" — including states like Fragile, Disconnected/Numb, and Courageous/Powerful.',
    plutchik: "Plutchik's Wheel of Emotions", plutchikDesc: 'Eight primary emotions in opposing pairs: joy-sadness, trust-disgust, fear-anger, surprise-anticipation.',
    musicTherapy: 'Music Therapy & Emotional Regulation', musicDesc: 'The "iso principle" — matching music to your current state, then gradually shifting — is backed by extensive research.',
  },
  hi: {
    hero: 'आपकी भावनात्मक तंदुरुस्ती, नए तरीके से', heroSub: 'विज्ञान-आधारित भावनात्मक स्वास्थ्य मंच।',
    howItWorks: 'कैसे काम करता है', step1: 'चेक इन', step2: 'मनन', step3: 'विकास',
    whatYouCanDo: 'आप क्या कर सकते हैं',
    uc1: 'दैनिक चेक-इन', uc1d: 'पहचानें कि आप कैसा महसूस करते हैं।', uc2: 'थेरेपी साथी', uc2d: 'अपने चिकित्सक के साथ भावना लॉग साझा करें।',
    uc3: 'मूड-आधारित संगीत', uc3d: 'आपकी भावनात्मक स्थिति से मेल खाने वाले गाने।', uc4: 'AI साथी', uc4d: 'कभी भी मोजो से बात करें।',
    uc5: 'पत्रिका', uc5d: 'निर्देशित चिंतन के साथ भावनाओं को गहराई से संसाधित करें।', uc6: 'समुदाय', uc6d: 'आप अकेले नहीं हैं।',
    inspiration: 'इस सप्ताह की प्रेरणा', science: 'विज्ञान', coaches: 'सर्वश्रेष्ठ से सीखें',
    floatingBtn: 'आप कैसा महसूस कर रहे हैं?',
    hoffman: 'हॉफमैन भावना श्रेणियाँ', hoffmanDesc: '18 सूक्ष्म भावनात्मक श्रेणियाँ।',
    plutchik: 'प्लुचिक का भावना चक्र', plutchikDesc: 'आठ प्राथमिक भावनाएँ विपरीत जोड़ों में।',
    musicTherapy: 'संगीत चिकित्सा', musicDesc: 'आइसो सिद्धांत — आपकी वर्तमान स्थिति से मिलान।',
  },
  es: {
    hero: 'Tu Bienestar Emocional, Reinventado', heroSub: 'Plataforma de salud emocional basada en la ciencia.',
    howItWorks: 'Cómo Funciona', step1: 'Registrar', step2: 'Reflexionar', step3: 'Crecer',
    whatYouCanDo: 'Qué Puedes Hacer',
    uc1: 'Check-ins Diarios', uc1d: 'Identifica cómo te sientes.', uc2: 'Compañero de Terapia', uc2d: 'Comparte registros con tu terapeuta.',
    uc3: 'Música por Estado', uc3d: 'Canciones que coinciden con tu estado emocional.', uc4: 'Compañero IA', uc4d: 'Habla con Mojo en cualquier momento.',
    uc5: 'Diario', uc5d: 'Procesa sentimientos profundamente.', uc6: 'Comunidad', uc6d: 'No estás solo/a.',
    inspiration: 'Inspiración de la Semana', science: 'La Ciencia', coaches: 'Aprende de los Mejores',
    floatingBtn: '¿Cómo te sientes?',
    hoffman: 'Categorías de Hoffman', hoffmanDesc: '18 categorías emocionales matizadas.',
    plutchik: 'Rueda de Plutchik', plutchikDesc: 'Ocho emociones primarias en pares opuestos.',
    musicTherapy: 'Musicoterapia', musicDesc: 'El principio "iso" — emparejar la música con tu estado actual.',
  },
  fr: {
    hero: 'Votre Bien-être Émotionnel, Réinventé', heroSub: 'Plateforme de santé émotionnelle fondée sur la science.',
    howItWorks: 'Comment Ça Marche', step1: 'Enregistrer', step2: 'Réfléchir', step3: 'Grandir',
    whatYouCanDo: 'Ce Que Vous Pouvez Faire',
    uc1: 'Check-ins Quotidiens', uc1d: 'Identifiez comment vous vous sentez.', uc2: 'Compagnon Thérapie', uc2d: 'Partagez vos émotions avec votre thérapeute.',
    uc3: 'Musique par Humeur', uc3d: 'Des chansons adaptées à votre état émotionnel.', uc4: 'Compagnon IA', uc4d: 'Parlez à Mojo à tout moment.',
    uc5: 'Journal', uc5d: 'Traitez vos sentiments en profondeur.', uc6: 'Communauté', uc6d: "Vous n'êtes pas seul(e).",
    inspiration: 'Inspiration de la Semaine', science: 'La Science', coaches: 'Apprenez des Meilleurs',
    floatingBtn: 'Comment vous sentez-vous?',
    hoffman: 'Catégories de Hoffman', hoffmanDesc: '18 catégories émotionnelles nuancées.',
    plutchik: 'Roue de Plutchik', plutchikDesc: 'Huit émotions primaires en paires opposées.',
    musicTherapy: 'Musicothérapie', musicDesc: 'Le principe "iso" — adapter la musique à votre état.',
  },
  de: {
    hero: 'Ihr Emotionales Wohlbefinden, Neu Gedacht', heroSub: 'Wissenschaftsbasierte Plattform für emotionale Gesundheit.',
    howItWorks: 'Wie Es Funktioniert', step1: 'Einchecken', step2: 'Reflektieren', step3: 'Wachsen',
    whatYouCanDo: 'Was Sie Tun Können',
    uc1: 'Tägliche Check-ins', uc1d: 'Erkennen Sie, wie Sie sich fühlen.', uc2: 'Therapiebegleiter', uc2d: 'Teilen Sie Emotionsprotokolle.',
    uc3: 'Stimmungsmusik', uc3d: 'Songs passend zu Ihrer Stimmung.', uc4: 'KI-Begleiter', uc4d: 'Sprechen Sie jederzeit mit Mojo.',
    uc5: 'Tagebuch', uc5d: 'Verarbeiten Sie Gefühle tiefgehend.', uc6: 'Gemeinschaft', uc6d: 'Sie sind nicht allein.',
    inspiration: 'Inspiration der Woche', science: 'Die Wissenschaft', coaches: 'Lernen Sie von den Besten',
    floatingBtn: 'Wie fühlen Sie sich?',
    hoffman: 'Hoffman-Kategorien', hoffmanDesc: '18 nuancierte emotionale Kategorien.',
    plutchik: 'Plutchiks Rad', plutchikDesc: 'Acht Grundemotionen in gegensätzlichen Paaren.',
    musicTherapy: 'Musiktherapie', musicDesc: 'Das "Iso-Prinzip" — Musik an Ihren Zustand anpassen.',
  },
  ar: {
    hero: 'صحتك العاطفية، بشكل جديد', heroSub: 'منصة صحة عاطفية مبنية على العلم.',
    howItWorks: 'كيف يعمل', step1: 'تسجيل', step2: 'تأمل', step3: 'نمو',
    whatYouCanDo: 'ما يمكنك فعله',
    uc1: 'تسجيل يومي', uc1d: 'حدد شعورك.', uc2: 'رفيق العلاج', uc2d: 'شارك سجلاتك مع معالجك.',
    uc3: 'موسيقى حسب المزاج', uc3d: 'أغاني تتناسب مع حالتك.', uc4: 'رفيق ذكي', uc4d: 'تحدث مع موجو في أي وقت.',
    uc5: 'يوميات', uc5d: 'عالج مشاعرك بعمق.', uc6: 'مجتمع', uc6d: 'لست وحدك.',
    inspiration: 'إلهام الأسبوع', science: 'العلم', coaches: 'تعلم من الأفضل',
    floatingBtn: 'كيف تشعر؟',
    hoffman: 'فئات هوفمان', hoffmanDesc: '18 فئة عاطفية دقيقة.',
    plutchik: 'عجلة بلوتشيك', plutchikDesc: 'ثماني مشاعر أساسية في أزواج متقابلة.',
    musicTherapy: 'العلاج بالموسيقى', musicDesc: 'مبدأ "الأيزو" — مطابقة الموسيقى لحالتك.',
  },
};
const MEDITATION_IMG = 'https://images.unsplash.com/photo-1763723341417-a4ea5ba6f675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBtaW5kZnVsbmVzcyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MDUxNDQ5NHww&ixlib=rb-4.1.0&q=85';
const HAPPY_IMG = 'https://images.unsplash.com/photo-1612723203798-321285cf5e3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxlbW90aW9uYWwlMjB3ZWxsYmVpbmclMjBoYXBweSUyMHBlb3BsZSUyMG5hdHVyZXxlbnwwfHx8fDE3NzA1MTQ0OTl8MA&ixlib=rb-4.1.0&q=85';
const ZEN_IMG = 'https://images.unsplash.com/photo-1764054655220-99548fc12fe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBtaW5kZnVsbmVzcyUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MDUxNDQ5NHww&ixlib=rb-4.1.0&q=85';

const ALL_COACHES = [
  { name: 'Dr. Brene Brown', focus: 'Vulnerability & Courage', url: 'https://brenebrown.com/', description: 'Research professor studying courage, vulnerability, and empathy.' },
  { name: 'Jay Shetty', focus: 'Mindfulness & Purpose', url: 'https://jayshetty.me/', description: 'Former monk, bestselling author of "Think Like a Monk".' },
  { name: 'Dr. Gabor Mate', focus: 'Trauma & Healing', url: 'https://drgabormate.com/', description: 'World-renowned speaker on addiction, stress, and childhood development.' },
  { name: 'Tara Brach', focus: 'Radical Acceptance', url: 'https://www.tarabrach.com/', description: 'Psychologist and meditation teacher, author of "Radical Acceptance".' },
  { name: 'Sadhguru', focus: 'Inner Engineering', url: 'https://isha.sadhguru.org/', description: 'Yogi and founder of Isha Foundation, advocating inner transformation.' },
  { name: 'Dr. Andrew Huberman', focus: 'Neuroscience', url: 'https://www.hubermanlab.com/', description: 'Stanford neuroscientist sharing science-based tools for everyday life.' },
  { name: 'Deepak Chopra', focus: 'Mind-Body Medicine', url: 'https://www.deepakchopra.com/', description: 'Pioneer of integrative medicine and spiritual wellbeing.' },
  { name: 'Eckhart Tolle', focus: 'Presence & Stillness', url: 'https://eckharttolle.com/', description: 'Author of "The Power of Now", teaching spiritual awakening.' },
  { name: 'Dr. Bessel van der Kolk', focus: 'Trauma Recovery', url: 'https://www.besselvanderkolk.com/', description: 'Author of "The Body Keeps the Score", leading trauma researcher.' },
  { name: 'Thich Nhat Hanh Foundation', focus: 'Mindful Living', url: 'https://plumvillage.org/', description: 'Zen master\'s legacy of mindfulness, peace, and compassion.' },
  { name: 'Dr. Dan Siegel', focus: 'Interpersonal Neurobiology', url: 'https://drdansiegel.com/', description: 'Clinical professor pioneering the science of mindful awareness.' },
  { name: 'Tony Robbins', focus: 'Peak Performance', url: 'https://www.tonyrobbins.com/', description: 'Life coach and motivational speaker on mastering emotions.' },
];

const ALL_VIDEOS = [
  { id: 'iCvmsMzlF7o', title: 'The Power of Vulnerability', speaker: 'Brene Brown', source: 'TED' },
  { id: 'w6T02g5hnT4', title: 'How to Make Stress Your Friend', speaker: 'Kelly McGonigal', source: 'TED' },
  { id: 'LnJwH_PZXnM', title: 'Emotional First Aid', speaker: 'Guy Winch', source: 'TED' },
  { id: 'rqoxYKtEWEc', title: 'Inside the Mind of a Master Procrastinator', speaker: 'Tim Urban', source: 'TED' },
  { id: '8KkKuTCFvzI', title: 'The Happy Secret to Better Work', speaker: 'Shawn Achor', source: 'TED' },
  { id: 'GZZ0zpUQhBQ', title: 'The Skill of Self Confidence', speaker: 'Dr. Ivan Joseph', source: 'TEDx' },
  { id: 'arj7oStGLkU', title: 'How Great Leaders Inspire Action', speaker: 'Simon Sinek', source: 'TED' },
  { id: 'Hu4Yvq-g7_Y', title: 'Sleep is Your Superpower', speaker: 'Matt Walker', source: 'TED' },
  { id: 'UUnRKf2CemA', title: 'How Breathing and Metabolism Are Connected', speaker: 'Ruben Meerman', source: 'TEDx' },
];

const MINDFULNESS_VIDEOS = [
  { id: 'w6T02g5hnT4', title: 'How to Make Stress Your Friend', speaker: 'Kelly McGonigal', source: 'TED' },
  { id: 'qzR62JJCMBQ', title: '10 Minute Guided Meditation', speaker: 'Goodful', source: 'Guided' },
  { id: '4PkrhH-bkpk', title: 'The Art of Stillness', speaker: 'Pico Iyer', source: 'TED' },
  { id: 'r5MIHkuPsmI', title: 'Mindfulness in Daily Life', speaker: 'Thich Nhat Hanh', source: 'Talk' },
  { id: 'ZEYuSRHgmCg', title: 'Practicing Mindfulness', speaker: 'Shauna Shapiro', source: 'TEDx' },
  { id: '3nwwKbM_vJc', title: 'How Mindfulness Changes the Brain', speaker: 'Richard Davidson', source: 'Talk' },
  { id: 'lhbdnDrWNYc', title: 'Body Scan Meditation', speaker: 'The Honest Guys', source: 'Guided' },
  { id: 'dEzbdLn2bJc', title: 'Mindful Movement Practice', speaker: 'Yoga With Adriene', source: 'Practice' },
  { id: 'ssss7V1_eyA', title: 'How to Be More Present', speaker: 'Jay Shetty', source: 'Talk' },
];

const BREATHWORK_VIDEOS = [
  { id: 'tybOi4hjZFQ', title: 'Breathwork for Beginners', speaker: 'Wim Hof', source: 'Tutorial' },
  { id: 'UUnRKf2CemA', title: 'Breathing and Metabolism', speaker: 'Ruben Meerman', source: 'TEDx' },
  { id: 'BAmPdmzMiXA', title: 'Box Breathing Technique', speaker: 'Mark Divine', source: 'Tutorial' },
  { id: 'YRPh_GaiL8s', title: '4-7-8 Breathing for Sleep', speaker: 'Dr. Andrew Weil', source: 'Tutorial' },
  { id: 'Tx1XPzgz1RA', title: 'Pranayama Breathing Practice', speaker: 'Sadhguru', source: 'Tutorial' },
  { id: 'u9Q8D0n2DwA', title: 'How Breath Control Can Change Your Life', speaker: 'Lucas Rockwood', source: 'TEDx' },
  { id: 'kgTL5G1ibIo', title: 'The Science of Breathing', speaker: 'Dr. Huberman', source: 'Podcast' },
  { id: 'enJyOTvEn4M', title: '5 Minute Breathwork Reset', speaker: 'Breathe & Flow', source: 'Practice' },
  { id: 'gz4G31LGyog', title: 'Alternate Nostril Breathing', speaker: 'Yoga With Adriene', source: 'Tutorial' },
];

const TEEN_STRESS_VIDEOS = [
  { id: 'RcGyVTAoXEU', title: 'Why Teens Are So Stressed', speaker: 'Lisa Damour', source: 'TED' },
  { id: 'NWH8N-BvhAw', title: 'Teen Mental Health Crisis', speaker: 'TEDMED', source: 'TED' },
  { id: 'DxIDKZHW3-E', title: 'What Adults Can Learn From Kids', speaker: 'Adora Svitak', source: 'TED' },
  { id: 'HL1MhBiSGKg', title: 'How to Help Teens With Anxiety', speaker: 'Dr. Becky Kennedy', source: 'Talk' },
  { id: '3bKuoH8CkFc', title: 'Parenting Anxious Teens', speaker: 'Dr. Lisa Damour', source: 'Talk' },
  { id: 'CbU8HvHnGiY', title: 'Social Media & Teen Wellbeing', speaker: 'Jonathan Haidt', source: 'Talk' },
  { id: 'vz-oHclGIS0', title: 'Building Resilience in Kids', speaker: 'Brene Brown', source: 'Talk' },
  { id: 'sWy1JTlFf8s', title: 'Helping Teens Manage Emotions', speaker: 'Dr. Dan Siegel', source: 'Talk' },
  { id: '8CrOL-ydFMI', title: 'Stress Management for Students', speaker: 'TEDx Youth', source: 'TEDx' },
];

// Rotate content weekly
function getWeekNumber() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
}

function getRotatedSlice(arr, count) {
  const week = getWeekNumber();
  const offset = (week * 3) % arr.length;
  const result = [];
  for (let i = 0; i < count && i < arr.length; i++) {
    result.push(arr[(offset + i) % arr.length]);
  }
  return result;
}

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-stone-200 flex-shrink-0 w-64 md:w-auto">
      <div className="relative aspect-video bg-stone-900">
        {playing ? (
          <iframe src={`https://www.youtube.com/embed/${video.id}?autoplay=1`} title={video.title} allow="autoplay; encrypted-media" allowFullScreen className="absolute inset-0 w-full h-full" />
        ) : (
          <button onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full group" data-testid={`play-video-${video.id}`}>
            <img src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} alt={video.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-5 h-5 text-hunter-green ml-0.5" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="p-3">
        <p className="text-[10px] text-hunter-green font-semibold uppercase tracking-wider">{video.source}</p>
        <h4 className="font-semibold text-xs mt-0.5 text-warm-charcoal line-clamp-1">{video.title}</h4>
        <p className="text-[10px] text-warm-charcoal/60">{video.speaker}</p>
      </div>
    </div>
  );
}

function ExpandableSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-50 transition-colors" style={{ touchAction: 'manipulation' }}>
        <h3 className="text-sm font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>{title}</h3>
        {open ? <ChevronUp className="w-4 h-4 text-warm-charcoal/50" /> : <ChevronDown className="w-4 h-4 text-warm-charcoal/50" />}
      </button>
      {open && <div className="px-4 pb-4 pt-0">{children}</div>}
    </div>
  );
}

const getUseCases = (tx) => [
  { icon: Heart, title: tx.uc1, desc: tx.uc1d },
  { icon: Brain, title: tx.uc2, desc: tx.uc2d },
  { icon: Music, title: tx.uc3, desc: tx.uc3d },
  { icon: MessageCircle, title: tx.uc4, desc: tx.uc4d },
  { icon: BookOpen, title: tx.uc5, desc: tx.uc5d },
  { icon: Users, title: tx.uc6, desc: tx.uc6d },
];

export default function HomePage({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const { language } = useLanguage();
  const tx = ABOUT_TEXT[language] || ABOUT_TEXT.en;
  const [dynamicVideos, setDynamicVideos] = useState(null);

  const weeklyCoaches = getRotatedSlice(ALL_COACHES, 6);
  const useCases = getUseCases(tx);

  useEffect(() => {
    axios.get(`${API}/moods/stats`).then(r => setStats(r.data)).catch(() => {});
    // Fetch dynamic videos
    axios.get(`${API}/videos`).then(r => setDynamicVideos(r.data)).catch(() => {});
    // One-time welcome toast ONLY for new signups
    const isNewSignup = sessionStorage.getItem('mojo_just_signed_up');
    if (isNewSignup) {
      sessionStorage.removeItem('mojo_just_signed_up');
      setTimeout(() => {
        toast('Welcome! Check your email for a welcome message. If you don\'t see it, check your Spam folder and mark MindMyMojo as a trusted sender.', { duration: 8000 });
      }, 2000);
    }
  }, []);

  // Use dynamic videos if available, fallback to static
  const inspirationVids = dynamicVideos?.inspiration?.length > 0 ? dynamicVideos.inspiration : getRotatedSlice(ALL_VIDEOS, 3);
  const mindfulnessVids = dynamicVideos?.mindfulness?.length > 0 ? dynamicVideos.mindfulness : getRotatedSlice(MINDFULNESS_VIDEOS, 3);
  const breathworkVids = dynamicVideos?.breathwork?.length > 0 ? dynamicVideos.breathwork : getRotatedSlice(BREATHWORK_VIDEOS, 3);
  const teenVids = dynamicVideos?.teens?.length > 0 ? dynamicVideos.teens : getRotatedSlice(TEEN_STRESS_VIDEOS, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-stone-50 to-stone-100 pb-24">
      <UnifiedMenu active="dashboard" stats={stats} currentQuote="" recentMoods={[]} onLogout={onLogout} />

      {/* Greeting */}
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-xl font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
          Welcome, {user?.name}
        </h1>
      </div>

      <main className="max-w-5xl mx-auto px-4">
        {/* Hero */}
        <section className="relative rounded-2xl overflow-hidden mb-6" data-testid="home-hero">
          <img src={HERO_IMG} alt="Wellness" className="w-full h-44 md:h-64 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex items-end">
            <div className="p-5">
              <h2 className="text-lg md:text-2xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {tx.hero}
              </h2>
              <p className="text-xs md:text-sm text-white/80 mt-1 max-w-lg">
                {tx.heroSub}
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-6" data-testid="home-how-it-works">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{tx.howItWorks}</h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { step: '01', title: tx.step1, img: ZEN_IMG },
              { step: '02', title: tx.step2, img: HAPPY_IMG },
              { step: '03', title: tx.step3, img: MEDITATION_IMG },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-xl overflow-hidden shadow-sm border border-stone-200">
                <img src={s.img} alt={s.title} className="w-full h-20 object-cover" />
                <div className="p-2 text-center">
                  <span className="text-lg font-bold text-hunter-green/20">{s.step}</span>
                  <p className="text-xs font-semibold text-warm-charcoal">{s.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-6" data-testid="home-use-cases">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{tx.whatYouCanDo}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <div key={uc.title} className="bg-white rounded-xl p-3 border border-stone-200">
                  <Icon className="w-4 h-4 text-hunter-green mb-1.5" />
                  <h4 className="font-semibold text-xs text-warm-charcoal">{uc.title}</h4>
                  <p className="text-[10px] text-warm-charcoal/60 mt-0.5 leading-relaxed">{uc.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Weekly Videos */}
        <section className="mb-6" data-testid="home-videos">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{tx.inspiration}</h3>
          <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible">
            {inspirationVids.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>

        {/* Mindfulness */}
        <section className="mb-6" data-testid="home-mindfulness">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">Mindfulness Training</h3>
          <p className="text-xs text-warm-charcoal/50 mb-3">Practice being present. These guided sessions help you develop awareness and inner calm.</p>
          <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible">
            {mindfulnessVids.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>

        {/* Breathwork */}
        <section className="mb-6" data-testid="home-breathwork">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">Breathwork & Techniques</h3>
          <p className="text-xs text-warm-charcoal/50 mb-3">Your breath is the most powerful tool you carry. Learn techniques to calm, energise, and heal.</p>
          <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible">
            {breathworkVids.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>

        {/* Teens & Parents */}
        <section className="mb-6" data-testid="home-teens">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">Teens, Parents & Stress</h3>
          <p className="text-xs text-warm-charcoal/50 mb-3">Understanding and supporting emotional wellness for teenagers and the parents who guide them.</p>
          <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-3 md:overflow-visible">
            {teenVids.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        </section>

        {/* Science */}
        <section className="mb-6 space-y-2" data-testid="home-science">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-2">{tx.science}</h3>
          <ExpandableSection title={tx.hoffman} defaultOpen={false}>
            <p className="text-xs text-warm-charcoal/70 leading-relaxed">{tx.hoffmanDesc}</p>
          </ExpandableSection>
          <ExpandableSection title={tx.plutchik}>
            <p className="text-xs text-warm-charcoal/70 leading-relaxed">{tx.plutchikDesc}</p>
          </ExpandableSection>
          <ExpandableSection title={tx.musicTherapy}>
            <p className="text-xs text-warm-charcoal/70 leading-relaxed">{tx.musicDesc}</p>
          </ExpandableSection>
        </section>

        {/* Coaches */}
        <section className="mb-6" data-testid="home-coaches">
          <h3 className="text-sm font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{tx.coaches}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {weeklyCoaches.map((c) => (
              <a key={c.name} href={c.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-3 border border-stone-200 hover:border-hunter-green/30 transition-colors group">
                <div className="flex items-start justify-between">
                  <h4 className="font-semibold text-xs text-warm-charcoal group-hover:text-hunter-green">{c.name}</h4>
                  <ExternalLink className="w-3 h-3 text-warm-charcoal/20 flex-shrink-0" />
                </div>
                <p className="text-[10px] text-hunter-green font-medium mt-0.5">{c.focus}</p>
                <p className="text-[10px] text-warm-charcoal/50 mt-1 line-clamp-2">{c.description}</p>
              </a>
            ))}
          </div>
        </section>
      </main>

      {/* Floating "How do you feel" button */}
      <button
        onClick={() => navigate('/check-in')}
        data-testid="floating-checkin-btn"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-hunter-green/90 backdrop-blur-md text-white shadow-2xl hover:bg-hunter-green transition-colors active:scale-95"
        style={{ touchAction: 'manipulation', boxShadow: '0 8px 32px rgba(58,90,64,0.4)' }}
      >
        <span className="text-sm" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.02em' }}>
          {tx.floatingBtn}
        </span>
      </button>
    </div>
  );
}
